<template>
  <div class="min-h-screen bg-[#080812] text-white overflow-x-hidden">

    <!-- ── GLOBAL ANIMATED BACKGROUND ─────────────────────────── -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden z-0">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <div class="orb orb-4"></div>
      <div class="orb orb-5"></div>
      <!-- Animated mesh gradient -->
      <div class="mesh-gradient"></div>
      <!-- Grid overlay -->
      <div class="absolute inset-0 bg-grid opacity-40"></div>
    </div>

    <!-- ── NAV ─────────────────────────────────────────────────── -->
    <header
      class="relative z-50 fixed top-0 inset-x-0 transition-all duration-500"
      :class="scrolled ? 'glass-nav border-b border-white/[0.06]' : ''"
    >
      <div class="max-w-7xl mx-auto px-5 sm:px-8">
        <div class="flex items-center justify-between h-[68px]">
          <router-link to="/" class="flex items-center gap-2.5 group">
            <img :src="logoUrl" alt="Promoter Master" class="w-8 h-8 object-contain" />
            <span class="font-bold text-white tracking-tight">Promoter Master</span>
          </router-link>

          <nav class="hidden md:flex items-center gap-1">
            <a v-for="nav in navLinks" :key="nav.label"
              :href="nav.href" :is="nav.to ? 'router-link' : 'a'" :to="nav.to"
              class="px-3.5 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-white/[0.06] transition-all duration-200"
            >{{ nav.label }}</a>
          </nav>

          <div class="flex items-center gap-2.5">
            <router-link to="/login" class="hidden sm:block px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
              Giriş yap
            </router-link>
            <router-link to="/signup" class="cta-btn px-5 py-2.5 text-sm font-semibold text-white rounded-xl">
              Ücretsiz başla
            </router-link>
          </div>
        </div>
      </div>
    </header>

    <!-- ── HERO ───────────────────────────────────────────────── -->
    <section class="relative z-10 min-h-screen flex flex-col items-center justify-center pt-20 pb-16 px-4">
      <!-- Top badge -->
      <div class="badge mb-8 flex items-center gap-2 px-4 py-1.5 rounded-full">
        <span class="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse"></span>
        <span class="text-xs font-medium text-violet-300">All-in-one dijital pazarlama platformu</span>
      </div>

      <!-- Headline -->
      <h1 class="text-center text-5xl sm:text-6xl md:text-[78px] font-black leading-[1.03] tracking-[-0.03em] mb-6 max-w-5xl">
        <span class="text-white">Tüm sosyal medyanızı</span><br/>
        <span class="gradient-text">tek yerden yönetin</span>
      </h1>

      <p class="text-center text-lg md:text-xl text-gray-400 mb-10 max-w-2xl leading-relaxed">
        Instagram, Facebook, TikTok ve LinkedIn'de içerik planlayın, analiz edin, ekibinizle iş birliği yapın — hepsi tek platformda.
      </p>

      <!-- CTAs -->
      <div class="flex flex-col sm:flex-row items-center gap-4 mb-14">
        <router-link to="/signup" class="group cta-btn-lg flex items-center gap-2.5 px-8 py-4 text-base font-semibold text-white rounded-2xl">
          Ücretsiz hesap oluştur
          <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
          </svg>
        </router-link>
        <router-link to="/pricing" class="glass-btn flex items-center gap-2 px-8 py-4 text-base font-medium text-gray-300 hover:text-white rounded-2xl transition-all duration-200">
          Fiyatları gör
        </router-link>
      </div>

      <!-- Platform pills -->
      <div class="flex items-center gap-3 flex-wrap justify-center mb-16">
        <span class="text-xs text-gray-600 font-medium uppercase tracking-wider">Desteklenen platformlar:</span>
        <div v-for="p in platforms" :key="p.name" class="platform-pill flex items-center gap-2 px-3 py-1.5 rounded-xl">
          <img v-if="p.img" :src="p.img" :alt="p.name" class="w-4 h-4 object-contain" />
          <span v-else class="w-4 h-4 flex-shrink-0" v-html="p.svgFull"></span>
          <span class="text-xs font-semibold text-gray-300">{{ p.name }}</span>
        </div>
      </div>

      <!-- Dashboard mock -->
      <div class="w-full max-w-5xl relative">
        <!-- Glow behind dashboard -->
        <div class="absolute -inset-4 bg-violet-600/10 rounded-3xl blur-3xl"></div>
        <div class="absolute -inset-1 bg-gradient-to-b from-violet-500/5 to-blue-500/5 rounded-2xl blur-xl"></div>

        <!-- Dashboard container -->
        <div class="relative dashboard-glass rounded-2xl overflow-hidden">
          <!-- Browser chrome -->
          <div class="flex items-center gap-2 px-5 py-3.5 border-b border-white/[0.06]">
            <div class="w-3 h-3 rounded-full bg-[#FF5F56]"></div>
            <div class="w-3 h-3 rounded-full bg-[#FFBD2E]"></div>
            <div class="w-3 h-3 rounded-full bg-[#27C93F]"></div>
            <div class="flex-1 mx-5 h-7 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center px-3 gap-2">
              <svg class="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
              <span class="text-xs text-gray-500 font-mono">app.promotermaster.com/dashboard</span>
            </div>
          </div>

          <!-- Dashboard body -->
          <div class="flex h-[340px] md:h-[420px]">
            <!-- Sidebar -->
            <div class="w-52 border-r border-white/[0.05] p-4 flex flex-col gap-1 hidden md:flex">
              <div class="flex items-center gap-2 px-3 py-2 mb-3">
                <img :src="logoUrl" alt="Promoter Master" class="w-6 h-6 object-contain" />
                <span class="text-xs font-bold text-white">Promoter</span>
              </div>
              <div v-for="item in sidebarItems" :key="item.label"
                class="flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-xs"
                :class="item.active ? 'bg-violet-500/15 text-violet-300 border border-violet-500/20' : 'text-gray-500 hover:bg-white/[0.03]'"
              >
                <div class="w-3.5 h-3.5 rounded" :class="item.active ? 'bg-violet-400' : 'bg-gray-700'"></div>
                {{ item.label }}
              </div>
            </div>

            <!-- Main area -->
            <div class="flex-1 p-5 overflow-hidden">
              <!-- Top row: stats -->
              <div class="grid grid-cols-4 gap-3 mb-4" ref="dashboardRef">
                <div v-for="(stat, i) in dashStats" :key="stat.label"
                  class="stat-card rounded-xl p-3"
                >
                  <div class="text-[10px] text-gray-500 mb-1 font-medium">{{ stat.label }}</div>
                  <div class="text-base font-black text-white leading-none mb-1 tabular-nums">{{ animatedDisplayValues[i] }}</div>
                  <div class="flex items-center gap-1">
                    <svg class="w-2.5 h-2.5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"/>
                    </svg>
                    <span class="text-[10px] text-emerald-400 font-semibold">{{ stat.change }}</span>
                  </div>
                </div>
              </div>

              <!-- Chart + sidebar -->
              <div class="grid grid-cols-3 gap-3 mb-3">
                <!-- Area chart mock -->
                <div class="col-span-2 stat-card rounded-xl p-3">
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-[11px] font-semibold text-white">Erişim & Etkileşim</span>
                    <div class="flex gap-2">
                      <span class="text-[10px] text-violet-400 font-medium">30 gün</span>
                    </div>
                  </div>
                  <div class="relative h-20">
                    <svg class="w-full h-full" viewBox="0 0 300 80" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#7C3AED" stop-opacity="0.4"/>
                          <stop offset="100%" stop-color="#7C3AED" stop-opacity="0"/>
                        </linearGradient>
                        <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#3B82F6" stop-opacity="0.3"/>
                          <stop offset="100%" stop-color="#3B82F6" stop-opacity="0"/>
                        </linearGradient>
                      </defs>
                      <g class="chart-wave-1">
                        <path :d="areaPath1" fill="url(#g1)"/>
                        <path :d="linePath1" fill="none" stroke="#7C3AED" stroke-width="1.5"/>
                      </g>
                      <g class="chart-wave-2">
                        <path :d="areaPath2" fill="url(#g2)"/>
                        <path :d="linePath2" fill="none" stroke="#3B82F6" stroke-width="1.5" stroke-dasharray="4 2"/>
                      </g>
                    </svg>
                  </div>
                </div>

                <!-- Platform breakdown -->
                <div class="stat-card rounded-xl p-3">
                  <div class="text-[11px] font-semibold text-white mb-3">Platformlar</div>
                  <div class="space-y-2.5">
                    <div v-for="pl in platformBreakdown" :key="pl.name">
                      <div class="flex justify-between mb-1">
                        <span class="text-[10px] text-gray-400">{{ pl.name }}</span>
                        <span class="text-[10px] font-semibold text-white">{{ pl.pct }}%</span>
                      </div>
                      <div class="h-1 bg-white/5 rounded-full overflow-hidden">
                        <div class="h-full rounded-full bar-shimmer" :style="`width:${pl.pct}%; background:${pl.color}; animation-delay:${pl.delay}ms`"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Post queue -->
              <div class="stat-card rounded-xl p-3">
                <div class="flex items-center justify-between mb-2.5">
                  <span class="text-[11px] font-semibold text-white">Planlanan Gönderiler</span>
                  <span class="text-[10px] text-violet-400 cursor-pointer hover:underline">Tümünü gör</span>
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <div v-for="post in scheduledPosts" :key="post.platform"
                    class="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]"
                  >
                    <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="`background:${post.color}`"></div>
                    <div>
                      <div class="text-[10px] text-white font-semibold leading-none mb-0.5">{{ post.platform }}</div>
                      <div class="text-[10px] text-gray-500">{{ post.time }}</div>
                    </div>
                    <div class="ml-auto px-1.5 py-0.5 rounded-md text-[9px] font-semibold" :class="post.statusClass">{{ post.status }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom fade -->
        <div class="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#080812] to-transparent pointer-events-none rounded-b-2xl"></div>
      </div>
    </section>

    <!-- ── STATS ──────────────────────────────────────────────── -->
    <section class="relative z-10 py-16">
      <div class="max-w-5xl mx-auto px-4">
        <div class="stats-bar rounded-2xl grid grid-cols-2 md:grid-cols-4">
          <div v-for="(stat, i) in heroStats" :key="stat.label"
            class="px-8 py-6 text-center"
            :class="i < 3 ? 'border-r border-white/[0.06]' : ''"
          >
            <div class="text-3xl md:text-4xl font-black text-white mb-1">{{ stat.value }}</div>
            <div class="text-xs text-gray-500 font-medium">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── FEATURES ─────────────────────────────────────────── -->
    <section id="features" class="relative z-10 py-28 px-4">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-20">
          <div class="badge inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6">
            <span class="text-xs font-semibold text-violet-400 uppercase tracking-widest">Özellikler</span>
          </div>
          <h2 class="text-4xl md:text-5xl font-black text-white leading-tight mb-5 tracking-tight">
            Başarılı olmak için<br/>
            <span class="gradient-text">ihtiyacınız olan her şey</span>
          </h2>
          <p class="text-lg text-gray-500 max-w-xl mx-auto">
            İçerik üretiminden analize, ekip çalışmasından otomasyona kadar tüm iş akışınızı tek çatı altında toplayın.
          </p>
        </div>

        <!-- Bento grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="feat in features" :key="feat.title"
            class="feature-card relative rounded-2xl p-7 group overflow-hidden"
            :class="feat.wide ? 'lg:col-span-2' : ''"
          >
            <!-- Hover glow -->
            <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
              :style="`background: radial-gradient(circle at 50% 0%, ${feat.glow}15 0%, transparent 70%)`"
            ></div>
            <!-- Border glow on hover -->
            <div class="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/[0.08] group-hover:ring-white/[0.15] transition-all duration-300"
              :style="`box-shadow: inset 0 0 30px ${feat.glow}08`"
            ></div>

            <div class="relative">
              <!-- Icon -->
              <div class="icon-wrap mb-5 w-11 h-11 rounded-xl flex items-center justify-center"
                :style="`background: ${feat.glow}20; border: 1px solid ${feat.glow}30`"
              >
                <span class="w-5 h-5" :style="`color:${feat.glow}`" v-html="feat.iconSvg"></span>
              </div>

              <h3 class="text-lg font-bold text-white mb-2.5">{{ feat.title }}</h3>
              <p class="text-sm text-gray-500 leading-relaxed mb-4">{{ feat.description }}</p>

              <div v-if="feat.tags" class="flex flex-wrap gap-2">
                <span v-for="tag in feat.tags" :key="tag"
                  class="px-2.5 py-1 text-[11px] font-semibold rounded-lg"
                  :style="`background:${feat.glow}12; color:${feat.glow}; border:1px solid ${feat.glow}25`"
                >{{ tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── PLATFORMS ─────────────────────────────────────────── -->
    <section id="platforms" class="relative z-10 py-24 px-4">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-14">
          <h2 class="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight">Tüm büyük platformlar, tek panel</h2>
          <p class="text-gray-500">Sevdiğiniz her platformu tek yerden yönetin</p>
        </div>
        <div class="grid grid-cols-3 sm:grid-cols-6 gap-4">
          <div v-for="platform in platformCards" :key="platform.name"
            class="platform-card group flex flex-col items-center gap-3 p-5 rounded-2xl cursor-default transition-all duration-300"
          >
            <div class="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
              :style="`background: ${platform.bg}`"
            >
              <img v-if="platform.img" :src="platform.img" :alt="platform.name" class="w-8 h-8 object-contain" />
              <span v-else class="w-8 h-8 flex items-center justify-center" v-html="platform.svgFull"></span>
            </div>
            <span class="text-sm font-semibold text-gray-400 group-hover:text-white transition-colors">{{ platform.name }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ── HOW IT WORKS ──────────────────────────────────────── -->
    <section id="how-it-works" class="relative z-10 py-28 px-4">
      <div class="max-w-5xl mx-auto">
        <div class="text-center mb-20">
          <div class="badge inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6">
            <span class="text-xs font-semibold text-violet-400 uppercase tracking-widest">Nasıl çalışır?</span>
          </div>
          <h2 class="text-4xl md:text-5xl font-black text-white tracking-tight">3 adımda başlayın</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          <div class="hidden md:block absolute top-14 left-[20%] right-[20%] h-px">
            <div class="w-full h-full bg-gradient-to-r from-transparent via-violet-500/40 to-transparent"></div>
          </div>
          <div v-for="(step, i) in steps" :key="i" class="step-card rounded-2xl p-7 text-center relative group">
            <div class="step-number absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white">
              {{ i + 1 }}
            </div>
            <div class="mt-3 mb-5 mx-auto w-16 h-16 rounded-2xl flex items-center justify-center"
              :style="`background:${step.bg}; border: 1px solid ${step.border}`"
            >
              <span class="w-7 h-7" :style="`color:${step.color}`" v-html="step.svgFull"></span>
            </div>
            <h3 class="text-base font-bold text-white mb-2">{{ step.title }}</h3>
            <p class="text-sm text-gray-500 leading-relaxed">{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── TESTIMONIALS ──────────────────────────────────────── -->
    <section class="relative z-10 py-24 px-4">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-14">
          <h2 class="text-3xl md:text-4xl font-black text-white tracking-tight">Kullanıcılarımız ne diyor?</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div v-for="t in testimonials" :key="t.name"
            class="testimonial-card rounded-2xl p-6 group hover:-translate-y-1 transition-transform duration-300"
          >
            <div class="flex gap-0.5 mb-4">
              <svg v-for="s in 5" :key="s" class="w-3.5 h-3.5 text-amber-400 fill-amber-400" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <p class="text-sm text-gray-400 mb-5 leading-relaxed">"{{ t.text }}"</p>
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white" :style="`background:${t.avatarColor}`">
                {{ t.name[0] }}
              </div>
              <div>
                <div class="text-sm font-semibold text-white">{{ t.name }}</div>
                <div class="text-xs text-gray-600">{{ t.role }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── CTA ───────────────────────────────────────────────── -->
    <section class="relative z-10 py-28 px-4">
      <div class="max-w-4xl mx-auto">
        <div class="cta-card relative rounded-3xl p-12 md:p-20 text-center overflow-hidden">
          <!-- Inner glow -->
          <div class="absolute inset-0 bg-gradient-to-br from-violet-600/[0.12] via-transparent to-blue-600/[0.08] rounded-3xl"></div>
          <div class="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-1 bg-gradient-to-r from-transparent via-violet-500/60 to-transparent"></div>

          <div class="relative">
            <h2 class="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight leading-tight">
              Dijital büyümenize<br/>hemen başlayın
            </h2>
            <p class="text-lg text-gray-400 mb-10 max-w-xl mx-auto">
              Ücretsiz hesap oluşturun. Kredi kartı gerekmez. 14 gün boyunca tüm özellikleri keşfedin.
            </p>
            <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
              <router-link to="/signup" class="group cta-btn-lg flex items-center gap-2.5 px-9 py-4 text-base font-semibold text-white rounded-2xl">
                Ücretsiz hesap oluştur
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </router-link>
              <router-link to="/pricing" class="text-gray-500 hover:text-white text-sm font-medium transition-colors">
                Fiyatlandırmayı gör →
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── FOOTER ────────────────────────────────────────────── -->
    <footer class="relative z-10 border-t border-white/[0.06] py-14 px-4">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
          <div class="col-span-2 md:col-span-2">
            <div class="flex items-center gap-2 mb-4">
              <img :src="logoUrl" alt="Promoter Master" class="w-7 h-7 object-contain" />
              <span class="font-bold text-white text-sm">Promoter Master</span>
            </div>
            <p class="text-sm text-gray-600 leading-relaxed max-w-xs">
              Tüm dijital pazarlama ihtiyaçlarınız için tasarlanmış tek platform.
            </p>
          </div>
          <div v-for="col in footerLinks" :key="col.title">
            <h4 class="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">{{ col.title }}</h4>
            <ul class="space-y-3">
              <li v-for="link in col.links" :key="link.label">
                <router-link :to="link.to" class="text-sm text-gray-600 hover:text-white transition-colors">{{ link.label }}</router-link>
              </li>
            </ul>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row items-center justify-between gap-3 pt-8 border-t border-white/[0.05]">
          <p class="text-xs text-gray-700">© 2026 Promoter Master. Tüm hakları saklıdır.</p>
          <span class="text-xs text-gray-800 font-mono">v{{ appVersion }}</span>
        </div>
      </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import packageJson from '../../package.json'
import instagramSvg from '@/assets/images/instagram.svg'
import facebookSvg from '@/assets/images/facebook.svg'
import tiktokSvg from '@/assets/images/tiktok.svg'
import linkedinSvg from '@/assets/images/linkedin.svg'
import { getPlatform } from '@/constants/platforms'
import logoUrl from '@/assets/logo.png'

const appVersion = computed(() => packageJson.version)
const scrolled = ref(false)
const onScroll = () => { scrolled.value = window.scrollY > 20 }

// Dashboard live counter
const dashboardRef = ref<HTMLElement | null>(null)
const dashStarted = ref(false)
const animatedValues = ref([0, 0, 0, 0])
const rawNums = [1284, 48200, 3700, 12400]
let tickerTimer: ReturnType<typeof setInterval> | null = null

const animatedDisplayValues = computed(() => animatedValues.value.map((v, i) => {
  if (i === 0) return v.toLocaleString('tr-TR')
  return (v / 1000).toFixed(1) + 'K'
}))

function countUp(targetIndex: number, target: number, duration = 1400) {
  const start = performance.now()
  const step = (now: number) => {
    const p = Math.min((now - start) / duration, 1)
    const ease = 1 - Math.pow(1 - p, 3)
    animatedValues.value[targetIndex] = Math.round(ease * target)
    if (p < 1) requestAnimationFrame(step)
    else if (targetIndex === rawNums.length - 1) startLiveTicker()
  }
  requestAnimationFrame(step)
}

function startLiveTicker() {
  dashStarted.value = true
  tickerTimer = setInterval(() => {
    const i = Math.floor(Math.random() * rawNums.length)
    const delta = (Math.random() > 0.5 ? 1 : -1) * Math.ceil(Math.random() * (i === 0 ? 3 : 30))
    animatedValues.value[i] = Math.max(rawNums[i] * 0.985, Math.min(rawNums[i] * 1.015, animatedValues.value[i] + delta))
  }, 1200)
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  rawNums.forEach((target, i) => setTimeout(() => countUp(i, target, 1200 + i * 80), 300 + i * 100))
})
onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  if (tickerTimer) clearInterval(tickerTimer)
})

const navLinks = [
  { label: 'Özellikler', href: '#features' },
  { label: 'Platformlar', href: '#platforms' },
  { label: 'Nasıl çalışır?', href: '#how-it-works' },
  { label: 'Fiyatlar', to: '/pricing', href: '/pricing' },
]

const platforms = [
  { name: 'Instagram', img: getPlatform('instagram')?.logo },
  { name: 'Facebook',  img: getPlatform('facebook')?.logo },
  { name: 'TikTok',   img: getPlatform('tiktok')?.logo },
  { name: 'LinkedIn',  img: getPlatform('linkedin')?.logo },
  { name: 'YouTube',   svgFull: getPlatform('youtube')?.svgNormal },
  { name: 'Twitter/X', svgFull: getPlatform('twitter')?.svgWhite },
]

const platformCards = [
  { name: 'Instagram', img: instagramSvg, bg: 'linear-gradient(135deg,rgba(225,48,108,0.18),rgba(253,121,36,0.12))' },
  { name: 'Facebook', img: facebookSvg, bg: 'rgba(24,119,242,0.15)' },
  { name: 'TikTok', img: tiktokSvg, bg: 'rgba(255,255,255,0.05)' },
  { name: 'LinkedIn', img: linkedinSvg, bg: 'rgba(0,119,181,0.15)' },
  { name: 'YouTube', svgFull: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#FF0000" viewBox="0 0 24 24"><path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2 31.3 31.3 0 000 12a31.3 31.3 0 00.5 5.8 3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1c.3-1.9.5-3.8.5-5.8a31.3 31.3 0 00-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/></svg>`, bg: 'rgba(255,0,0,0.12)' },
  { name: 'Twitter/X', svgFull: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L2.25 2.25H8.08l4.261 5.636 5.903-5.636zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`, bg: 'rgba(255,255,255,0.07)' },
]

const sidebarItems = [
  { label: 'Dashboard', active: true },
  { label: 'Gönderiler', active: false },
  { label: 'Takvim', active: false },
  { label: 'Analitik', active: false },
  { label: 'Medya', active: false },
  { label: 'SEO', active: false },
  { label: 'Blog', active: false },
]

const dashStats = [
  { label: 'Toplam Gönderi', value: '1,284', change: '+12%' },
  { label: 'Erişim', value: '48.2K', change: '+8.3%' },
  { label: 'Etkileşim', value: '3.7K', change: '+21%' },
  { label: 'Takipçi', value: '12.4K', change: '+342' },
]

const platformBreakdown = [
  { name: 'Instagram', pct: 42, color: '#E1306C', delay: 400 },
  { name: 'Facebook', pct: 28, color: '#1877F2', delay: 600 },
  { name: 'LinkedIn', pct: 18, color: '#0077B5', delay: 800 },
  { name: 'TikTok', pct: 12, color: '#69C9D0', delay: 1000 },
]

const scheduledPosts = [
  { platform: 'Instagram', time: 'Bugün 14:00', status: 'Planlandı', color: '#E1306C', statusClass: 'bg-violet-500/15 text-violet-300' },
  { platform: 'Facebook', time: 'Bugün 16:30', status: 'Taslak', color: '#1877F2', statusClass: 'bg-yellow-500/15 text-yellow-300' },
  { platform: 'LinkedIn', time: 'Yarın 09:00', status: 'Planlandı', color: '#0077B5', statusClass: 'bg-violet-500/15 text-violet-300' },
]

// SVG area/line paths for chart
const pts1 = [0,55, 30,48, 60,52, 90,38, 120,42, 150,28, 180,32, 210,20, 240,24, 270,15, 300,18]
const pts2 = [0,65, 30,60, 60,62, 90,55, 120,58, 150,48, 180,50, 210,42, 240,45, 270,38, 300,40]
const buildPath = (pts: number[], close = false) => {
  let d = `M ${pts[0]} ${pts[1]}`
  for (let i = 2; i < pts.length; i += 2) d += ` L ${pts[i]} ${pts[i+1]}`
  if (close) d += ` L ${pts[pts.length-2]} 80 L 0 80 Z`
  return d
}
const linePath1 = buildPath(pts1)
const areaPath1 = buildPath(pts1, true)
const linePath2 = buildPath(pts2)
const areaPath2 = buildPath(pts2, true)

const heroStats = [
  { value: '10K+', label: 'Aktif kullanıcı' },
  { value: '1M+', label: 'Planlanan gönderi' },
  { value: '6+', label: 'Desteklenen platform' },
  { value: '99.9%', label: 'Uptime garantisi' },
]

const featureIcon = (paths: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">${paths}</svg>`

const features = [
  {
    title: 'Akıllı İçerik Planlama',
    description: 'Tüm platformlar için tek takvimde planlayın. AI destekli optimal zamanlama, toplu CSV import ve cross-platform içerik yönetimi.',
    glow: '#7C3AED',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>'),
    tags: ['Toplu import', 'Optimal zamanlama', 'Çoklu platform'],
    wide: true,
  },
  {
    title: 'Gelişmiş Analitik',
    description: 'IG, FB, Twitter, LinkedIn, YouTube API entegrasyonu ile günlük metrik senkronizasyonu ve PDF rapor export.',
    glow: '#3B82F6',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>'),
    wide: false,
  },
  {
    title: 'Ekip İş Birliği',
    description: 'Onay akışları, gerçek zamanlı iş birliği ve rol bazlı erişim kontrolüyle ekibinizle sorunsuz çalışın.',
    glow: '#10B981',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>'),
    wide: false,
  },
  {
    title: 'Medya Kütüphanesi',
    description: 'Tüm fotoğraf ve videolarınızı organize edin. Dahili görsel editör, video thumbnail seçici ve medya tag sistemi.',
    glow: '#F59E0B',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>'),
    wide: false,
  },
  {
    title: 'SEO & Blog Yönetimi',
    description: 'Google Search Console entegrasyonu, keyword ranking takibi, teknik SEO crawl ve blog SEO skoru. Tiptap WYSIWYG editör ile içerik üretin, RSS ile otomatikleştirin.',
    glow: '#06B6D4',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>'),
    tags: ['GSC Entegrasyon', 'Keyword Tracking', 'Blog Editör', 'RSS Otomasyon'],
    wide: true,
  },
  {
    title: 'Otomasyon & Sıra',
    description: 'BullMQ ile güçlendirilmiş akıllı gönderi sırası, şablonlar ve RSS ile tam otomasyon.',
    glow: '#EC4899',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/>'),
    wide: false,
  },
  {
    title: 'E-posta & SMS Kampanyaları',
    description: 'E-posta kampanyaları ve Twilio entegrasyonlu SMS kampanyaları ile kitlenize doğrudan ulaşın.',
    glow: '#8B5CF6',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>'),
    wide: false,
  },
  {
    title: 'Link Tracker',
    description: 'GA Measurement Protocol + Meta Pixel entegrasyonu ile link performansını ve dönüşümlerinizi takip edin.',
    glow: '#6366F1',
    iconSvg: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>'),
    wide: false,
  },
]

const steps = [
  {
    title: 'Platformlarınızı bağlayın',
    desc: 'Instagram, Facebook, TikTok ve diğer platformlarınızı OAuth ile saniyeler içinde bağlayın.',
    bg: 'rgba(124,58,237,0.12)', border: 'rgba(124,58,237,0.3)', color: '#A78BFA',
    svgFull: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>'),
  },
  {
    title: 'İçerik oluşturun & planlayın',
    desc: 'Güçlü editörle içeriklerinizi oluşturun, AI önerilerinden yararlanın ve optimal saatlerde planlayın.',
    bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', color: '#60A5FA',
    svgFull: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>'),
  },
  {
    title: 'Büyümeyi analiz edin',
    desc: 'Gerçek zamanlı analizlerle hangi içeriklerin işe yaradığını görün ve stratejinizi optimize edin.',
    bg: 'rgba(6,182,212,0.12)', border: 'rgba(6,182,212,0.3)', color: '#22D3EE',
    svgFull: featureIcon('<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>'),
  },
]

const testimonials = [
  { text: 'Tüm sosyal medya hesaplarımı tek yerden yönetmek inanılmaz kolaylık sağladı. Haftalık içerik planımı artık 30 dakikada yapıyorum.', name: 'Ayşe K.', role: 'İçerik Üreticisi', avatarColor: '#7C3AED' },
  { text: 'Analitik raporlar sayesinde hangi içeriklerin işe yaradığını gördük. Etkileşim oranımız 3 ayda %40 arttı.', name: 'Mehmet D.', role: 'Dijital Pazarlama Müdürü', avatarColor: '#2563EB' },
  { text: 'Ekip onay akışı harika. Artık e-posta trafiği olmadan içerikleri inceleyip onaylayabiliyoruz.', name: 'Selin A.', role: 'Sosyal Medya Uzmanı', avatarColor: '#059669' },
]

const footerLinks = [
  { title: 'Ürün', links: [{ label: 'Özellikler', to: '/#features' }, { label: 'Fiyatlandırma', to: '/pricing' }, { label: 'Yardım', to: '/help' }] },
  { title: 'Hukuki', links: [{ label: 'Gizlilik', to: '/privacy-policy' }, { label: 'Kullanım Şartları', to: '/terms-of-service' }, { label: 'KVKK', to: '/gdpr' }, { label: 'Çerezler', to: '/cookie-policy' }] },
  { title: 'Şirket', links: [{ label: 'İletişim', to: '/contact' }] },
]
</script>

<style>
/* ── ORB ANIMATIONS ─────────────────────────────────────── */
.orb {
  position: absolute;
  border-radius: 50%;
  will-change: transform;
}
.orb-1 {
  width: 700px; height: 700px;
  background: radial-gradient(circle at 40% 40%, rgba(124,58,237,0.55) 0%, rgba(124,58,237,0.15) 40%, transparent 70%);
  filter: blur(80px);
  top: -250px; left: -180px;
  animation: orb1 18s ease-in-out infinite;
}
.orb-2 {
  width: 600px; height: 600px;
  background: radial-gradient(circle at 60% 40%, rgba(59,130,246,0.45) 0%, rgba(99,102,241,0.12) 45%, transparent 70%);
  filter: blur(70px);
  top: 20%; right: -200px;
  animation: orb2 22s ease-in-out infinite;
}
.orb-3 {
  width: 500px; height: 500px;
  background: radial-gradient(circle at 50% 50%, rgba(236,72,153,0.40) 0%, rgba(168,85,247,0.10) 45%, transparent 70%);
  filter: blur(80px);
  bottom: 10%; left: -100px;
  animation: orb3 26s ease-in-out infinite;
}
.orb-4 {
  width: 450px; height: 450px;
  background: radial-gradient(circle at 50% 50%, rgba(6,182,212,0.35) 0%, rgba(59,130,246,0.08) 50%, transparent 70%);
  filter: blur(70px);
  bottom: -150px; right: 5%;
  animation: orb4 20s ease-in-out infinite;
}
.orb-5 {
  width: 300px; height: 300px;
  background: radial-gradient(circle at 50% 50%, rgba(16,185,129,0.30) 0%, transparent 65%);
  filter: blur(60px);
  top: 50%; left: 40%;
  animation: orb5 15s ease-in-out infinite;
}

@keyframes orb1 {
  0%   { transform: translate(0,0) scale(1); }
  20%  { transform: translate(80px,-100px) scale(1.08); }
  40%  { transform: translate(-60px,-60px) scale(0.92); }
  60%  { transform: translate(100px,60px) scale(1.05); }
  80%  { transform: translate(-40px,100px) scale(0.96); }
  100% { transform: translate(0,0) scale(1); }
}
@keyframes orb2 {
  0%   { transform: translate(0,0) scale(1); }
  25%  { transform: translate(-100px,80px) scale(1.1); }
  50%  { transform: translate(60px,120px) scale(0.90); }
  75%  { transform: translate(-80px,-60px) scale(1.06); }
  100% { transform: translate(0,0) scale(1); }
}
@keyframes orb3 {
  0%   { transform: translate(0,0) scale(1); }
  30%  { transform: translate(120px,-80px) scale(0.88); }
  60%  { transform: translate(-60px,-120px) scale(1.12); }
  100% { transform: translate(0,0) scale(1); }
}
@keyframes orb4 {
  0%   { transform: translate(0,0) scale(1); }
  33%  { transform: translate(-80px,60px) scale(1.08); }
  66%  { transform: translate(80px,-80px) scale(0.93); }
  100% { transform: translate(0,0) scale(1); }
}
@keyframes orb5 {
  0%   { transform: translate(0,0) scale(1) rotate(0deg); }
  50%  { transform: translate(-150px,-100px) scale(1.3) rotate(180deg); }
  100% { transform: translate(0,0) scale(1) rotate(360deg); }
}

/* ── MESH GRADIENT ───────────────────────────────────────── */
.mesh-gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 40% at 20% 20%, rgba(124,58,237,0.07) 0%, transparent 60%),
    radial-gradient(ellipse 60% 50% at 80% 80%, rgba(59,130,246,0.07) 0%, transparent 60%);
  animation: meshShift 12s ease-in-out infinite alternate;
}
@keyframes meshShift {
  0%   { opacity: 0.6; transform: scale(1) rotate(0deg); }
  50%  { opacity: 1; transform: scale(1.05) rotate(1deg); }
  100% { opacity: 0.7; transform: scale(0.97) rotate(-1deg); }
}

/* ── GRID ────────────────────────────────────────────────── */
.bg-grid {
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
}

/* ── GLASS NAV ───────────────────────────────────────────── */
.glass-nav {
  background: rgba(8,8,18,0.82);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* ── LOGO ────────────────────────────────────────────────── */
/* ── BADGE ───────────────────────────────────────────────── */
.badge {
  background: rgba(124,58,237,0.08);
  border: 1px solid rgba(124,58,237,0.2);
}

/* ── GRADIENT TEXT ───────────────────────────────────────── */
.gradient-text {
  background: linear-gradient(135deg, #A78BFA 0%, #60A5FA 50%, #34D399 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── CTA BUTTON ──────────────────────────────────────────── */
.cta-btn {
  background: linear-gradient(135deg, #7C3AED, #5B21B6);
  box-shadow: 0 0 30px rgba(124,58,237,0.3), 0 4px 15px rgba(0,0,0,0.3);
  transition: all 0.2s ease;
}
.cta-btn:hover {
  box-shadow: 0 0 50px rgba(124,58,237,0.5), 0 8px 25px rgba(0,0,0,0.4);
  transform: translateY(-1px);
}

.cta-btn-lg {
  background: linear-gradient(135deg, #7C3AED, #4F46E5);
  box-shadow: 0 0 40px rgba(124,58,237,0.35), 0 6px 20px rgba(0,0,0,0.4);
  transition: all 0.2s ease;
}
.cta-btn-lg:hover {
  box-shadow: 0 0 60px rgba(124,58,237,0.55), 0 10px 30px rgba(0,0,0,0.5);
  transform: translateY(-2px);
}

/* ── GLASS BUTTON ────────────────────────────────────────── */
.glass-btn {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
}
.glass-btn:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.18);
}

/* ── PLATFORM PILLS ──────────────────────────────────────── */
.platform-pill {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
}

/* ── DASHBOARD ───────────────────────────────────────────── */
.dashboard-glass {
  background: rgba(15,15,30,0.85);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(20px);
  box-shadow:
    0 0 0 1px rgba(124,58,237,0.1),
    0 50px 100px rgba(0,0,0,0.6),
    0 20px 60px rgba(124,58,237,0.08);
}

.stat-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
}

/* ── STATS BAR ───────────────────────────────────────────── */
.stats-bar {
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06);
  backdrop-filter: blur(10px);
}

/* ── FEATURE CARDS ───────────────────────────────────────── */
.feature-card {
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.07);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}
.feature-card:hover {
  background: rgba(255,255,255,0.035);
  transform: translateY(-3px);
  box-shadow: 0 20px 60px rgba(0,0,0,0.4);
}

/* ── PLATFORM CARDS ──────────────────────────────────────── */
.platform-card {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.07);
  transition: all 0.3s ease;
}
.platform-card:hover {
  background: rgba(255,255,255,0.05);
  border-color: rgba(255,255,255,0.15);
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
}

/* ── STEP CARDS ──────────────────────────────────────────── */
.step-card {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.07);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}
.step-card:hover {
  border-color: rgba(124,58,237,0.3);
  box-shadow: 0 0 30px rgba(124,58,237,0.08);
}

.step-number {
  background: linear-gradient(135deg, #7C3AED, #4F46E5);
  box-shadow: 0 0 20px rgba(124,58,237,0.4);
  font-size: 11px;
}

/* ── TESTIMONIALS ────────────────────────────────────────── */
.testimonial-card {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.07);
  backdrop-filter: blur(10px);
}
.testimonial-card:hover {
  border-color: rgba(255,255,255,0.12);
}

/* ── CTA CARD ────────────────────────────────────────────── */
.cta-card {
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(124,58,237,0.2);
  backdrop-filter: blur(20px);
  box-shadow: 0 0 80px rgba(124,58,237,0.08);
}

/* ── DASHBOARD: STAT CARD FLOAT ──────────────────────────── */
.stat-card { will-change: transform; }
.stat-card:nth-child(1) { animation: statFloat 5s ease-in-out infinite 0s; }
.stat-card:nth-child(2) { animation: statFloat 5s ease-in-out infinite 0.8s; }
.stat-card:nth-child(3) { animation: statFloat 5s ease-in-out infinite 1.6s; }
.stat-card:nth-child(4) { animation: statFloat 5s ease-in-out infinite 2.4s; }

@keyframes statFloat {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-3px); }
}

/* ── DASHBOARD: CHART WAVE ───────────────────────────────── */
.chart-wave-1 {
  animation: chartWave1 6s ease-in-out infinite;
  transform-origin: 50% 100%;
}
.chart-wave-2 {
  animation: chartWave2 8s ease-in-out infinite;
  transform-origin: 50% 100%;
}
@keyframes chartWave1 {
  0%, 100% { transform: translateY(0px) scaleY(1); }
  30%       { transform: translateY(-3px) scaleY(1.04); }
  60%       { transform: translateY(2px) scaleY(0.97); }
}
@keyframes chartWave2 {
  0%, 100% { transform: translateY(0px) scaleY(1); }
  40%       { transform: translateY(2px) scaleY(0.96); }
  70%       { transform: translateY(-2px) scaleY(1.03); }
}

/* ── DASHBOARD: BAR SHIMMER ──────────────────────────────── */
.bar-shimmer {
  position: relative;
  overflow: hidden;
  animation: barPulse 3s ease-in-out infinite;
}
.bar-shimmer::after {
  content: '';
  position: absolute;
  top: 0; left: -100%; bottom: 0;
  width: 60%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
  animation: shimmerSlide 2.5s ease-in-out infinite;
}
@keyframes barPulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.8; }
}
@keyframes shimmerSlide {
  0%   { left: -60%; }
  100% { left: 140%; }
}
</style>
